import React from "react";
import { Box, Typography } from "@mui/joy";
import { useParams } from "react-router-dom";
import { DocumentPreviewWidgetV2 } from "../../";
import { useDocument, useAsset } from "../../../hooks/query";
import { useNotification } from "../../../hooks/useNotification";
import { useNetworkStatus } from "../../../hooks/useNetworkStatus";

function getGeneratedAssetId(data: { AssetId?: string; assetId?: string } | null | undefined) {
    return data?.AssetId ?? data?.assetId;
}

export function Documents({ projectId: projectIdProp }: { projectId?: string }) {
    const { id: routeProjectId } = useParams<{ id: string }>();
    const projectId = projectIdProp || routeProjectId;
    const isOnline = useNetworkStatus();
    const isOfflineProject =
        !isOnline || !!projectId?.startsWith("offline-");

    const { useInitialInspectionCreate, useClaimsEstimateCreate, useInitialInspectionPhotoReportCreate } =
        useDocument();
    const generateInspectionSvc = useInitialInspectionCreate();
    const generateClaimsSvc = useClaimsEstimateCreate();
    const generatePhotoReportSvc = useInitialInspectionPhotoReportCreate();
    const { showSuccess, showError } = useNotification();

    const { useDownload } = useAsset();
    const downloadInspectionAssetSvc = useDownload();
    const downloadClaimsAssetSvc = useDownload();
    const downloadPhotoReportAssetSvc = useDownload();

    const handleDownloadInitialInspection = async () => {
        if (!projectId || isOfflineProject) return;
        try {
            const res = await generateInspectionSvc.mutateAsync({ ProjectId: projectId });
            const assetId = getGeneratedAssetId(res);
            if (assetId) {
                await downloadInspectionAssetSvc.mutateAsync({ AssetId: assetId });
            }
        } catch (e) {
            console.error(e);
        }
    };

    const handleDownloadClaimsEstimate = async () => {
        if (!projectId || isOfflineProject) return;
        try {
            const res = await generateClaimsSvc.mutateAsync({ ProjectId: projectId });
            const assetId = getGeneratedAssetId(res);
            if (assetId) {
                await downloadClaimsAssetSvc.mutateAsync({ AssetId: assetId });
            }
        } catch (e) {
            console.error(e);
        }
    };

    const handleDownloadPhotoReport = async () => {
        if (!projectId || isOfflineProject) return;
        try {
            const res = await generatePhotoReportSvc.mutateAsync({ ProjectId: projectId });
            const assetId = getGeneratedAssetId(res);
            if (assetId) {
                await downloadPhotoReportAssetSvc.mutateAsync({ AssetId: assetId });
                showSuccess("Inspection photo report downloaded successfully");
            }
        } catch (e) {
            console.error(e);
            showError("Failed to generate photo report");
        }
    };

    if (isOfflineProject) {
        return (
            <Box>
                <Box mb={2}>
                    <Typography level="title-md">Documents</Typography>
                    <Typography level="body-sm" color="neutral">
                        Report generation is available after this project syncs
                        online.
                    </Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box>
            <Box mb={2}>
                <Typography level="title-md">Documents</Typography>
                <Typography level="body-sm" color="neutral">
                    Generated reports and related documentation.
                </Typography>
            </Box>

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(auto-fill, 320px)",
                    },
                    gap: 2.5,
                }}
            >
                <DocumentPreviewWidgetV2
                    title="Initial Inspection Report.pdf"
                    description="Click to generate and download report"
                    fileType="PDF"
                    loading={generateInspectionSvc.isPending || downloadInspectionAssetSvc.isPending}
                    onButtonClick={handleDownloadInitialInspection}
                />
                <DocumentPreviewWidgetV2
                    title="Claims Report.pdf"
                    description="Click to generate and download report"
                    fileType="PDF"
                    loading={generateClaimsSvc.isPending || downloadClaimsAssetSvc.isPending}
                    onButtonClick={handleDownloadClaimsEstimate}
                />
                <DocumentPreviewWidgetV2
                    title="Inspection Photo Report.pdf"
                    description="All uploaded inspection photos by category"
                    fileType="PDF"
                    loading={generatePhotoReportSvc.isPending || downloadPhotoReportAssetSvc.isPending}
                    onButtonClick={handleDownloadPhotoReport}
                />
            </Box>
        </Box>
    );
}
